import React from "react";
import { Modal, Button, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteMenuCategoryMutation } from "@me-team/host/main/menu/graphql/menu.hooks";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess, MenuCategoryProxy } from "@me-team/host/main/graphql/types";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface DeleteCategoryModalProps {
    show: boolean;
    handleClose: () => void;
    category: MenuCategoryProxy;
    onDelete: () => void;
    toggleShowToast?: (text: string) => void;
    canDeleteRestoreCategory?: boolean;
}

const DeleteCategoryModal: React.FC<DeleteCategoryModalProps> = ({
    show,
    handleClose,
    category,
    onDelete,
    toggleShowToast,
    canDeleteRestoreCategory,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const [deleteMenuCategory] = useDeleteMenuCategoryMutation();
    const canDeleteRestoreCategoryByRole = useRoleAccess(CompanyAccess.MenuDishCategoryDeleteRestore);
    const canSubmitDeleteRestore = canDeleteRestoreCategory ?? canDeleteRestoreCategoryByRole;
    const nameVm = buildTranslationTextVm({
        translatedValue: category?.name,
        originValue: category?.nameOrigin,
        isTranslationMode,
    });

    const handleDeleteCategory = async () => {
        if (!canSubmitDeleteRestore) return;

        await deleteMenuCategory({
            variables: {
                id: category.id,
                branchId: Number(branchId),
            },
        });

        toggleShowToast?.(t("Deleted"));
        handleClose();
        onDelete();
    };

    const getVariant = () => {
        if (category?.numberOfItems > 0) return "variant2";
        if (category?.childCategories?.length === 0 || !category?.childCategories) return "variant1";
        if (category?.childCategories?.every((sub) => sub?.numberOfItems === 0)) return "variant3";
        return "variant4";
    };

    const variant = getVariant();

    const handleToListDishes = () => navigate(`/menu/dishes/branch/${branchId}`);

    const renderHeaderModal = (title: React.ReactNode) => (
        <Modal.Header closeButton className="border-bottom-0">
            <Modal.Title className="fs-5">{title}</Modal.Title>
        </Modal.Header>
    );

    const renderBodyModal = (text: string) => (
        <Modal.Body className="border-bottom-0">
            <Col xs={12} md={11} className="mx-md-auto text-center">
                {text}
            </Col>
        </Modal.Body>
    );

    const renderModalContent = () => {
        switch (variant) {
            case "variant1":
                return (
                    <>
                        {renderHeaderModal(
                            <>
                                {t("Delete category")} {nameVm.displayText}
                                {nameVm.isMissingTranslation ? (
                                    <TranslationMissingDot tooltipText={t("Edit the language version")} />
                                ) : null}
                            </>
                        )}
                        {renderBodyModal(
                            t(
                                "The category will be moved to the archive and you will be able to restore it at any time. Are you sure you want to delete this category?"
                            )
                        )}
                        <Modal.Footer className="border-top-0">
                            <Col>
                                <Button variant="outline-primary" className="w-100" onClick={handleClose}>
                                    {t("Cancel")}
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    variant="primary"
                                    className="w-100 fw-bold"
                                    disabled={!canSubmitDeleteRestore}
                                    onClick={handleDeleteCategory}
                                >
                                    {t("Delete category")}
                                </Button>
                            </Col>
                        </Modal.Footer>
                    </>
                );
            case "variant2":
                return (
                    <>
                        {renderHeaderModal(t("Unable to Delete Category"))}
                        {renderBodyModal(
                            t(
                                "This category is associated with dishes. Please edit the dishes linked to this category before deletion."
                            )
                        )}
                        <Modal.Footer className="border-top-0">
                            <Button variant="primary" className="w-100" onClick={handleToListDishes}>
                                {t("List of dishes")}
                            </Button>
                        </Modal.Footer>
                    </>
                );
            case "variant3":
                return (
                    <>
                        {renderHeaderModal(
                            <>
                                {t("Delete category")} {nameVm.displayText}
                                {nameVm.isMissingTranslation ? (
                                    <TranslationMissingDot tooltipText={t("Edit the language version")} />
                                ) : null}
                            </>
                        )}
                        {renderBodyModal(
                            t(
                                "This category has subcategories which will also be deleted. Please move them if you wish to keep them."
                            )
                        )}
                        <Modal.Footer className="border-top-0">
                            <Col>
                                <Button variant="outline-primary" className="w-100" onClick={handleClose}>
                                    {t("Cancel")}
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    variant="primary"
                                    className="w-100 fw-bold"
                                    disabled={!canSubmitDeleteRestore}
                                    onClick={handleDeleteCategory}
                                >
                                    {t("Delete category")}
                                </Button>
                            </Col>
                        </Modal.Footer>
                    </>
                );
            case "variant4":
                return (
                    <>
                        {renderHeaderModal(t("Unable to Delete Category"))}
                        {renderBodyModal(
                            t(
                                "This category has subcategories that are linked to dishes. Move the subcategories linked to dishes to delete the category."
                            )
                        )}
                        <Modal.Footer className="border-top-0">
                            <Button variant="primary" className="w-100 fw-bold" onClick={handleClose}>
                                {t("okBtn")}
                            </Button>
                        </Modal.Footer>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal centered show={show} onHide={handleClose}>
            {renderModalContent()}
        </Modal>
    );
};

export default DeleteCategoryModal;
